import { byId } from '../utils/dom.js';
import { eventBus } from '../eventBus.js';
import { color } from './color.js';
import { number } from '../utils/number.js';
import { i18n } from '../i18n.js';

export const CLASSIFICATION_CONTINUOUS = 1,
  CLASSIFICATION_DISCRETE = 2,
  CLASSIFICATION_EQUIDISTANT = 3;

export const classification = {

  domId: 'viewSetting-palette',

  domNameType: 'setting-palette-type',
  domIdNumClasses: 'setting-num-classes',

  domNode: null,

  /**
   * @type {JsonStatReader} NAFIDAS JSON-stat reader
   */
  _reader: null,

  /**
   * @type {module:gui/legendChoropleth}
   */
  _legend: null,

  /**
   * Initialize the view settings.
   * @param {JsonStatReader} reader
   * @param {DataSeries} data
   */
  async init (reader, data) {
    await i18n.init(i18n.getLang());
    this._reader = reader;
    this.rowData = data;
    this.domNode = byId(this.domId);
    this._create();
    this._initEvents();
  },

  /**
   * Initialize events.
   * @emits eventBus~gradientChange
   * @private
   */
  _initEvents () {
    this.domNode.addEventListener('input', (evt) => {
      if (evt.target.id === this.domIdNumClasses) {
        this.updateGradientClassified(this.rowData);
      }
      eventBus.dispatch('gradientChange');
    });
  },

  /**
   * Create the DOM of the color gradients.
   * @private
   */
  _create () {
    this.domNode.append(i18n.t('colors') + ':');

    let gradient, count = this.getMaxColors(this.rowData);

    gradient = color.getGradient(this.rowData, CLASSIFICATION_CONTINUOUS);
    this._createGradient(gradient, i18n.t('continuous'), true);

    gradient = color.getGradient(this.rowData, CLASSIFICATION_DISCRETE, count);
    const div = this._createGradient(gradient, i18n.t('classified'));

    const label = document.createElement('label');
    const input = document.createElement('input');
    input.type = 'number';
    input.min = 2;
    input.max = count;
    input.id = this.domIdNumClasses;
    input.value = input.max;
    label.append(i18n.t('colors'), input);
    div.lastChild.before(label);

    gradient = color.getGradient(this.rowData, CLASSIFICATION_EQUIDISTANT);
    this._createGradient(gradient, i18n.t('discrete'));

    this._createGradientTicks();
    this._createDomains();
  },

  /**
   * Create and append a gradient container.
   * A gradient container consists of a label, an input radio and a gradient.
   * @param {Gradient} gradient
   * @param {String} txt
   * @param {Boolean} [checked]
   * @return {HTMLDivElement}
   * @private
   */
  _createGradient (gradient, txt, checked = false) {
    const div1 = document.createElement('div');
    div1.classList.add('gradient-container');
    const label = document.createElement('label');
    const input = document.createElement('input');
    input.name = this.domNameType;
    input.type = 'radio';
    input.checked = checked;
    input.value = gradient.type;
    const div2 = this._createGradientStep(gradient);
    label.append(input, txt);
    div1.append(label, div2);
    this.domNode.append(div1);
    return div1;
  },

  /**
   * Create the gradient
   * @param {Gradient} gradient chroma scale function
   * @return {HTMLDivElement}
   * @private
   */
  _createGradientStep (gradient) {
    const count = 100,
      div = document.createElement('div'),
      step = (this.rowData.max - this.rowData.min) / count;

    div.classList.add('gradient');
    for (let i = 0; i < count; i++) {
      const span = document.createElement('span');
      span.classList.add('gradient__step');
      span.style.backgroundColor = gradient.scale(this.rowData.min + i * step);
      div.append(span);
    }

    return div;
  },

  /**
   * Create and append the tick marks to each gradient.
   * @private
   */
  _createGradientTicks () {
    document.querySelectorAll('.gradient-container .gradient')
      .forEach(el => {
        let span;
        span = document.createElement('span');
        span.classList.add('gradient__tick', 'gradient__tick_left');
        el.append(span);

        span = document.createElement('span');
        span.classList.add('gradient__tick', 'gradient__tick_middle');
        el.append(span);

        span = document.createElement('span');
        span.classList.add('gradient__tick', 'gradient__tick_right');
        el.append(span);
      });
  },

  /**
   * Create and append the domains to the last gradient.
   * @private
   */
  _createDomains () {
    let val;
    const div = document.querySelector('.gradient-container:last-child .gradient');

    val = this.rowData.min;
    this._createGradientDomain(val, 'gradient__domain_left', div);

    let numDecimals = this._reader.getDecimals();
    val = this.rowData.min + (this.rowData.max - this.rowData.min) / 2;
    numDecimals = numDecimals < number.getPlaces(val) ? numDecimals + 1 : numDecimals;
    val = number.roundTo(val, numDecimals);
    this._createGradientDomain(val, 'gradient__domain_middle', div);

    val = this.rowData.max;
    this._createGradientDomain(val, 'gradient__domain_right', div);

    const span = document.createElement('span');
    span.classList.add('gradient__label');
    span.append(i18n.t('domain'));
    div.append(span);
  },

  /**
   * Create a gradient domain.
   * @param {Number} val
   * @param {String} cssClass
   * @param {HTMLElement} parent
   * @return {HTMLSpanElement}
   * @private
   */
  _createGradientDomain (val, cssClass, parent) {
    const span = document.createElement('span');
    span.classList.add('gradient__domain', cssClass);
    span.append(val);
    parent.append(span);

    return span;
  },

  _getCheckedRadio () {
    return document.querySelector('input[name=' + this.domNameType + ']:checked');
  },

  /**
   * Returns the gradient currently selected.
   * @return {Gradient}
   */
  getGradient () {
    const type = this.getType();
    let count = null;
    if (type === CLASSIFICATION_DISCRETE) {
      count = this.getNumColors();
    }
    return color.getGradient(this.rowData, type, count);
  },

  /**
   * Return the type of classification
   * @return {Number}
   */
  getType () {
    const el = this._getCheckedRadio();
    return Number(el.value);
  },

  /**
   * Return the chosen number of colors.
   * Returns the number of colors (the value attribute) from the input element of type number.
   * @return {Number}
   */
  getNumColors () {
    return Number(byId(this.domIdNumClasses).value);
  },

  /**
   * Return the maximum allowed number of color classes.
   * @param {DataSeries} data
   */
  getMaxColors (data) {
    let max = data.series.length;
    return max < 10 ? max : 10;
  },

  /**
   * Update the gradient.
   * @param {DataSeries} data
   */
  updateGradientContinuous (data) {
    this.rowData = data;

    const el = this.domNode.querySelector('.gradient-container:nth-of-type(1) .gradient');
    const gradient = color.getGradient(this.rowData, CLASSIFICATION_CONTINUOUS);
    const div = this._createGradientStep(gradient);
    el.replaceWith(div);
  },

  /**
   * Update the gradient.
   * @param {DataSeries} data
   */
  updateGradientClassified (data) {
    this.rowData = data;

    const el = this.domNode.querySelector('.gradient-container:nth-of-type(2) .gradient');
    const count = this.getNumColors();
    const gradient = color.getGradient(this.rowData, CLASSIFICATION_DISCRETE, count);
    const div = this._createGradientStep(gradient);
    el.replaceWith(div);
  },

  /**
   * Update the gradient.
   * @param {DataSeries} data
   */
  updateGradientDiscrete (data) {
    this.rowData = data;

    const el = this.domNode.querySelector('.gradient-container:nth-of-type(3) .gradient');
    const gradient = color.getGradient(this.rowData, CLASSIFICATION_EQUIDISTANT);
    const div = this._createGradientStep(gradient);
    el.replaceWith(div);
    this._createGradientTicks();
    this._createDomains();
  }
};